const REGIONS = [
    { name: 'Bratislavský kraj', lat: 48.15, lng: 17.13 },
    { name: 'Trnavský kraj', lat: 48.37, lng: 17.58 },
    { name: 'Trenčiansky kraj', lat: 48.75, lng: 18.04 },
    { name: 'Nitriansky kraj', lat: 48.19, lng: 18.09 },
    { name: 'Žilinský kraj', lat: 49.22, lng: 18.74 },
    { name: 'Banskobystrický kraj', lat: 48.65, lng: 19.45 },
    { name: 'Prešovský kraj', lat: 49.00, lng: 21.24 },
    { name: 'Košický kraj', lat: 48.72, lng: 21.26 },
]

const MAX_DEGREE_DISTANCE = 1.8

export function resolveRegion(lat, lng) {
    const latitude = parseFloat(lat)
    const longitude = parseFloat(lng)

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
        return 'unknown'
    }

    let closest = null
    let minDistance = Infinity

    for (const region of REGIONS) {
        const dLat = latitude - region.lat
        const dLng = longitude - region.lng
        const distance = Math.sqrt(dLat * dLat + dLng * dLng)
        if (distance < minDistance) {
            minDistance = distance
            closest = region.name
        }
    }

    if (minDistance > MAX_DEGREE_DISTANCE) {
        return 'unknown'
    }

    return closest
}

function stripDiacritics(value) {
    return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

const REGION_ALIASES = {
    bratislavsky: 'Bratislavský kraj',
    bratislava: 'Bratislavský kraj',
    trnavsky: 'Trnavský kraj',
    trnava: 'Trnavský kraj',
    trenciansky: 'Trenčiansky kraj',
    trencin: 'Trenčiansky kraj',
    nitriansky: 'Nitriansky kraj',
    nitra: 'Nitriansky kraj',
    zilinsky: 'Žilinský kraj',
    zilina: 'Žilinský kraj',
    banskobystricky: 'Banskobystrický kraj',
    'banska bystrica': 'Banskobystrický kraj',
    presovsky: 'Prešovský kraj',
    presov: 'Prešovský kraj',
    kosicky: 'Košický kraj',
    kosice: 'Košický kraj',
}

export function canonicalRegion(name) {
    if (!name) return null
    const key = stripDiacritics(name)
        .toLowerCase()
        .replace(/self-governing region|region|kraj/g, '')
        .trim()
    return REGION_ALIASES[key] || null
}

function cleanString(value) {
    if (value == null) return null
    const text = String(value).trim()
    return text.length ? text : null
}

function findComponent(components, type) {
    const match = (components || []).find((item) => item.types?.includes(type))
    return match ? match.long_name : null
}

function cityFromHyphenated(value) {
    if (!value) return null
    const trimmed = String(value).trim()
    if (!trimmed.includes('-')) return cleanString(trimmed)
    const main = trimmed.split('-')[0].trim()
    return main && !/^\d/.test(main) ? cleanString(main) : cleanString(trimmed)
}

function cityFromFormattedAddress(formattedAddress) {
    if (!formattedAddress) return null
    const parts = String(formattedAddress).split(',')
    for (const part of parts) {
        const text = part.trim()
        const postalMatch = text.match(/^\d{3}\s?\d{2}\s+(.+)$/)
        const candidate = postalMatch ? postalMatch[1].trim() : text
        if (candidate.includes('-')) {
            const main = cityFromHyphenated(candidate)
            if (main) return main
        }
    }
    return null
}

export function cityFromGeocode(components = [], formattedAddress = '') {
    const fromAddress = cityFromFormattedAddress(formattedAddress)
    if (fromAddress) return fromAddress

    const locality = findComponent(components, 'locality')
    const postalTown = findComponent(components, 'postal_town')

    return (
        cityFromHyphenated(locality) ||
        cityFromHyphenated(postalTown) ||
        cleanString(locality) ||
        cleanString(postalTown) ||
        cleanString(findComponent(components, 'sublocality_level_1')) ||
        cleanString(findComponent(components, 'administrative_area_level_2'))
    )
}

export function geoFromAddressComponents(components = [], formattedAddress = '') {
    return {
        regionName: findComponent(components, 'administrative_area_level_1'),
        district: findComponent(components, 'administrative_area_level_2'),
        city: cityFromGeocode(components, formattedAddress),
        postalCode: findComponent(components, 'postal_code'),
    }
}

export function buildGeo(input = {}) {
    const { regionName, district, city, postalCode, lat, lng, source } = input
    const region = canonicalRegion(regionName) || resolveRegion(lat, lng)
    const latitude = lat != null ? parseFloat(lat) : null
    const longitude = lng != null ? parseFloat(lng) : null

    return {
        region: region || 'unknown',
        district: cleanString(district),
        city: cleanString(city),
        postal_code: cleanString(postalCode),
        lat: Number.isNaN(latitude) ? null : latitude,
        lng: Number.isNaN(longitude) ? null : longitude,
        source: source || null,
    }
}
